import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink, Info, ListChecks, RefreshCw, X } from 'lucide-react';
import api from '../services/api';
import { ErrorBox, Skeleton } from '../ui/kit';
import { datumLangUndZeit, datumUndZeit, wochentagKurzMitZeit } from '../lib/zeit';
import './PayoutReview.css';

// ADMIN-ABRECHNUNG-PRUEFANSICHT-001 — Prüfansicht einer Abrechnungsperiode. Nur lesen.
//
// Der Owner will eine berechnete Woche VOR der Freigabe prüfen: Fahrten, Bar, Online, Gebühren
// je Anbieter, besondere Zeilen, Bonus und die Rechnung bis zur Auszahlung. Alle Zahlen kommen
// vom Server aus der gespeicherten Periode, ihren Buchungszeilen und Gebührennachweisen
// (GET /admin/payouts/:id/review). Diese Oberfläche rechnet nichts nach und löst nichts aus.
//
// Wortwahl (Vertrag src/finanzwortlaut.test.js, ein Name je Zahl auf jeder Oberfläche):
//   ledger_effect    → „Kontobewegung bei VOOGO". Die Karte nannte die Zeilenwirkung „Wirkung";
//                      ein zweiter Name für dieselbe Zahl hätte die Verwechslung neu erzeugt.
//                      Payouts.js trägt das Wort weiterhin nicht — die Ansicht ist eine eigene
//                      Datei und ein eigenes Fenster außerhalb der Abrechnungshistorie.
//   psp_cost_cents   → „Zahlungsdienstleisterkosten" · carry_in → „Anfangssaldo"
//   carry_out        → „Schlusssaldo" (negativ, wie in der Historie) · payout → „Auszahlungsbetrag"
//   statement_total  → „ergibt zusammen … aufgeteilt in" (wie die Einnahmen-Seite des Partner-Portals):
//                      bei einer negativen Woche wäre „= Auszahlungsbetrag 0,00 €" eine falsche Gleichung.
//   Stückzahl        → „Buchungszeilen" · take + service → „Take + Service" (wie in der Historie)

const STATE_LABELS = {
  pending: 'Zu prüfen – noch nicht freigegeben',
  rejected: 'Abgelehnter Prüfstand',
  approved: 'Freigegebener Prüfstand',
  calculated: 'Berechnet – noch nicht freigegeben',
  ready: 'Freigegeben',
  exported: 'Exportiert',
  paid: 'Ausgezahlt',
};

const EVENT_LABELS = {
  completed: 'Fahrt',
  cancellation_fee: 'Stornogebühr',
  no_show_fee: 'Nichtantritt',
  tip: 'Trinkgeld',
  reversal: 'Gegenbuchung',
  manual_correction: 'Korrektur',
  bonus_info: 'Bonus (nur Info)',
  shortfall_recovery: 'Rückfluss Fahrpreisrest',
  opening_balance: 'Eröffnungsstand',
  wallet_purchase_fee_refund: 'Guthabenkauf-Gebühr erstattet',
  wallet_purchase_fee_correction: 'Guthabenkauf-Gebühr korrigiert',
};

// Nur diese Ereignisse zählen als Fahrt und tragen einen VOOGO-Anteil (financeLedger.js TERMINAL_RIDE_EVENT_TYPES).
const RIDE_EVENTS = ['completed', 'cancellation_fee', 'no_show_fee'];

const PAYMENT_LABELS = {
  cash: 'Bar',
  card: 'Karte',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  online: 'Online',
  wallet: 'Guthaben',
};

const PROVIDER_LABELS = { stripe: 'Stripe/Karte', paypal: 'PayPal' };

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const money = (value) => `${Number(value || 0).toLocaleString('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} €`;
const cents = (value) => money(Number(value || 0) / 100);
// Abzüge stehen negativ wie in der Abrechnungshistorie; die Null bleibt „0,00 €", nie „-0,00 €".
const minusCents = (value) => cents(value ? -value : 0);
const optionalCents = (value) => (value ? cents(value) : '');

const driverLabel = (row) => row.driverName
  || (row.driverId ? `Fahrer ${String(row.driverId).slice(0, 8)}` : 'Partnerkonto');
const paymentLabel = (line) => (line.paymentProvider === 'paypal'
  ? 'PayPal'
  : PAYMENT_LABELS[line.paymentMethod] || '–');

const CONTROL_ROWS = [
  { key: 'lineCount', label: 'Buchungszeilen', kind: 'count' },
  { key: 'rideCount', label: 'Fahrten', kind: 'count', rides: true },
  { key: 'cashCents', label: 'Bar (bei den Fahrern)' },
  { key: 'onlineCents', label: 'Online gesamt' },
  // collectedOnline − Guthaben, wie „Online erfolgreich eingezogen" im verbindlichen PDF — enthält PayPal.
  { key: 'cardCollectedCents', label: 'davon Karte und PayPal, erfolgreich eingezogen', indent: true },
  { key: 'walletCents', label: 'davon VOOGO-Guthaben', indent: true },
  { group: 'Kartenzahlungen je Anbieter' },
  ...['stripe', 'paypal'].flatMap((provider) => [
    { key: `${provider}.transactionCount`, label: `${PROVIDER_LABELS[provider]}: Zahlungen`, kind: 'count', indent: true },
    { key: `${provider}.transactionGrossCents`, label: `${PROVIDER_LABELS[provider]}: Betrag`, indent: true },
    { key: `${provider}.feeCents`, label: `${PROVIDER_LABELS[provider]}: Gebühr`, sign: -1, indent: true },
  ]),
  { key: 'walletPurchaseFeeCents', label: 'Zahlungsabwicklungskosten aus Guthabenkäufen', sign: -1, indent: true, hideIfZero: true },
  { group: 'VOOGO-Anteil, Korrekturen, Bonus' },
  { key: 'takeServiceCents', label: 'Take + Service (VOOGO-Anteil)' },
  { key: 'correctionCents', label: 'Korrekturen (inkl. Gegenbuchungen)' },
  { key: 'shortfallRecoveryCents', label: 'davon Rückfluss beglichener Fahrpreisreste', indent: true, hideIfZero: true },
  { key: 'feeSettlementCashCents', label: 'Bar mitkassierte Stornogebühren (gehören VOOGO)' },
  { key: 'tipCents', label: 'Trinkgeld', hideIfZero: true },
  { key: 'bonusCents', label: 'Bonus Partner an Fahrer (nur Info, kein VOOGO-Geld)' },
  { group: 'Rechnung bis zur Auszahlung' },
  { key: 'ledgerEffectCents', label: 'Kontobewegung bei VOOGO' },
  { key: 'pspCostCents', label: '− Zahlungsdienstleisterkosten', sign: -1 },
  { key: 'carryInCents', label: '+ Anfangssaldo', naText: 'aus der Vorwoche' },
  { key: 'statementTotalCents', label: '= ergibt zusammen', strong: true },
  { key: 'payoutCents', label: 'aufgeteilt in: Auszahlungsbetrag', indent: true, strong: true },
  { key: 'carryOutCents', label: 'aufgeteilt in: Schlusssaldo', sign: -1, indent: true },
];

function formatCheckValue(value, row) {
  if (value == null) return '–';
  if (row.kind === 'count') return String(value);
  return row.sign === -1 ? minusCents(value) : cents(value);
}

function Verdict({ check, row }) {
  if (check?.equal === true) return <span className="payout-review-ok"><CheckCircle2 size={12} /> stimmt</span>;
  if (check?.equal === false) return <span className="payout-review-bad"><AlertTriangle size={12} /> weicht ab</span>;
  return <span className="payout-review-na">{row.naText || 'nur gespeichert'}</span>;
}

function lineHints(line) {
  const hints = [];
  if (line.reversal) {
    const art = EVENT_LABELS[line.reversal.originalEventType] || 'Buchungszeile';
    hints.push(`Gegenbuchung zu ${art} vom ${datumUndZeit(line.reversal.originalOccurredAt, { jahr: false, leer: 'unbekannt' })}: `
      + `macht nur deren Kontobewegung bei VOOGO rückgängig (${cents(line.reversal.originalLedgerEffectCents)}).`
      + (RIDE_EVENTS.includes(line.reversal.originalEventType)
        ? ' Die Fahrt zählt weiter bei den Fahrten, und der VOOGO-Anteil (Take + Service) bleibt.'
        : ''));
    if (!line.reversal.originalInThisPeriod) hints.push('Die ursprüngliche Buchungszeile liegt in einer früheren Abrechnung.');
  }
  if (line.feeSettlementCashCents > 0) {
    hints.push(`Stornogebühr bar mitkassiert: ${cents(line.feeSettlementCashCents)}. `
      + 'Das Geld gehört VOOGO und mindert die Kontobewegung bei VOOGO dieser Fahrt.');
  }
  // Bar gewählt, aber voll über das Kundenkonto gebucht (rideCancellation.feePaymentComponents,
  // Nichtantritt ebenso): ohne diesen Satz liest sich „Zahlart Bar" neben „Guthaben" wie ein Fehler.
  const walletBookedCash = line.paymentMethod === 'cash' && line.grossCents > 0 && line.walletCents === line.grossCents;
  if (line.eventType === 'cancellation_fee') {
    hints.push(walletBookedCash
      ? 'Stornogebühr: zählt als Fahrt. Bei Bar wird sie dem Kundenkonto (Guthaben) belastet.'
      : 'Stornogebühr: zählt als Fahrt.');
  }
  if (line.eventType === 'no_show_fee') {
    hints.push(walletBookedCash
      ? 'Nichtantritt: zählt als Fahrt. Bei Bar wird die Gebühr dem Kundenkonto (Guthaben) belastet.'
      : 'Nichtantritt: zählt als Fahrt.');
  }
  if (line.eventType === 'manual_correction' && !line.driverId) {
    hints.push('Korrektur am Partnerkonto, keinem Fahrer zugeordnet.');
  }
  if (line.beforePeriodStart) {
    hints.push('Liegt vor diesem Zeitraum: war beim Berechnen noch keiner Abrechnung zugeordnet und wurde deshalb mitgenommen.');
  }
  if (line.pspFeeProblem) hints.push(`Gebührennachweis fehlt oder widerspricht: ${line.pspFeeProblem}`);
  return hints;
}

function RideLink({ rideId }) {
  if (!rideId) return <span className="payout-review-na">–</span>;
  return (
    <a
      className="payout-review-ride"
      href={`/rides?ride=${encodeURIComponent(rideId)}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Fahrt ${rideId} öffnen`}
      title={rideId}
    >
      {String(rideId).slice(0, 8)} <ExternalLink size={11} />
    </a>
  );
}

function Checklist({ review, windowStart, windowEnd, markedCount }) {
  const { checks, rideBreakdown } = review;
  const value = (key) => checks[key]?.stored;
  const optional = (amount) => (amount == null ? '–' : cents(amount));
  const payments = (count) => (count == null ? '– Zahlungen' : `${count} ${count === 1 ? 'Zahlung' : 'Zahlungen'}`);
  const providerText = ['stripe', 'paypal'].map((provider) => (
    `${PROVIDER_LABELS[provider]}: ${payments(value(`${provider}.transactionCount`))}, `
    + `${optional(value(`${provider}.transactionGrossCents`))}, Gebühr ${optional(value(`${provider}.feeCents`))}`
  )).join(' · ');
  const feeCash = value('feeSettlementCashCents');
  return (
    <ol className="payout-review-checklist">
      <li><strong>Fahrten</strong>{value('rideCount')} Fahrten: {rideBreakdown.completed} abgeschlossen, {rideBreakdown.cancellationFee} Storno, {rideBreakdown.noShowFee} Nichtantritt. Stimmen die Fahrten je Fahrer (Tabelle „Je Fahrer“)?</li>
      <li><strong>Bar</strong>{cents(value('cashCents'))} Fahrpreis haben die Fahrer bar eingenommen{feeCash > 0 ? `, dazu ${cents(feeCash)} bar mitkassierte Stornogebühren` : ''}. Passt der Barbetrag je Fahrer (Tabelle „Je Fahrer“)?</li>
      <li><strong>Online (Karte, Guthaben)</strong>{cents(value('onlineCents'))} online: Karte und PayPal erfolgreich eingezogen {cents(value('cardCollectedCents'))}, VOOGO-Guthaben {cents(value('walletCents'))}.</li>
      <li><strong>Transaktionsgebühren je Anbieter</strong>{providerText}. Zeitfenster für den Abgleich im Stripe- oder PayPal-Konto: {windowStart} bis {windowEnd} (Berlin).</li>
      <li><strong>Besondere Zeilen</strong>{markedCount} gelb markierte Buchungszeilen: Korrekturen, Gegenbuchungen, Stornogebühren und Nichtantritte. Ist jede davon gewollt?</li>
      <li><strong>Bonus (nur Information)</strong>{cents(value('bonusCents'))} Bonus vom Partner an Fahrer. Kein VOOGO-Geld, ändert die Auszahlung nicht.</li>
      <li><strong>Rechnung bis zur Auszahlung</strong>Kontobewegung bei VOOGO {cents(value('ledgerEffectCents'))} − Zahlungsdienstleisterkosten {cents(value('pspCostCents'))} + Anfangssaldo {cents(value('carryInCents'))} ergibt zusammen {cents(value('statementTotalCents'))}, aufgeteilt in Auszahlungsbetrag {cents(value('payoutCents'))} und Schlusssaldo {minusCents(value('carryOutCents'))}.</li>
    </ol>
  );
}

function Banner({ review }) {
  const { reconciliation, allChecksEqual, authority, feeProblems, period } = review;
  const notes = [];
  if (reconciliation.status === 'error') {
    notes.push(<p key="error" className="payout-review-banner is-bad"><AlertTriangle size={14} /> Abgleich nicht möglich: {reconciliation.message}. Nicht freigeben, erst klären.</p>);
  } else if (reconciliation.status === 'mismatch' || !allChecksEqual) {
    notes.push(<p key="mismatch" className="payout-review-banner is-bad"><AlertTriangle size={14} /> Gespeicherte Abrechnung und Buchungszeilen stimmen nicht centgenau überein. Nicht freigeben, erst klären.</p>);
  } else if (review.source === 'weekly_snapshot') {
    notes.push(<p key="snapshot" className="payout-review-banner is-info"><Info size={14} /> {review.snapshotNotice}</p>);
  } else {
    notes.push(<p key="match" className="payout-review-banner is-ok"><CheckCircle2 size={14} /> Gespeicherte Abrechnung, Buchungszeilen und Belege stimmen centgenau überein.</p>);
  }
  if (feeProblems > 0) {
    notes.push(<p key="fees" className="payout-review-banner is-bad"><AlertTriangle size={14} /> {feeProblems} Buchungszeile(n) ohne gültigen Gebührennachweis.</p>);
  }
  if (authority.operable !== true) {
    notes.push(<p key="blocked" className="payout-review-banner is-bad"><AlertTriangle size={14} /> Gesperrt: {authority.reason || 'Finanzperiode gesperrt'}</p>);
  }
  // Bewusst ohne Knopfnamen: der Freigabeweg der Abrechnung ändert sich mit der Wochenabrechnung,
  // der Satz soll dann nicht auf einen Knopf zeigen, den es nicht mehr gibt.
  if (period.state === 'calculated' && authority.canMarkReady === true && reconciliation.status === 'match' && allChecksEqual) {
    notes.push(<p key="next" className="payout-review-banner is-info"><Info size={14} /> Wenn alles stimmt: dieses Fenster schließen und die Woche in der Abrechnung freigeben.</p>);
  }
  return <div className="payout-review-banners">{notes}</div>;
}

export default function PayoutReview({ payout, onClose }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [onlyMarked, setOnlyMarked] = useState(false);
  // Beim Rendern gemerkt (vor dem autoFocus des Schließen-Knopfs): dorthin kehrt der Fokus zurück.
  const [returnFocusTo] = useState(() => (typeof document === 'undefined' ? null : document.activeElement));
  const generation = useRef(0);
  const dialogRef = useRef(null);

  const load = useCallback(() => {
    const current = ++generation.current;
    setLoading(true);
    setLoadError(false);
    return api.get(payout.review_id
      ? `/admin/payouts/reviews/${encodeURIComponent(payout.review_id)}/review`
      : `/admin/payouts/${encodeURIComponent(payout.id)}/review`)
      .then((response) => {
        if (current !== generation.current) return;
        setReview(response.data.review);
      })
      .catch(() => {
        if (current !== generation.current) return;
        setLoadError(true);
      })
      .finally(() => {
        if (current === generation.current) setLoading(false);
      });
  }, [payout.id, payout.review_id]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  useEffect(() => () => {
    if (returnFocusTo && typeof returnFocusTo.focus === 'function' && document.contains(returnFocusTo)) {
      returnFocusTo.focus();
    }
  }, [returnFocusTo]);

  // Escape schließt; Tab bleibt im Fenster. Ohne Falle erreichte Shift+Tab die verdeckten
  // Knöpfe der Abrechnungshistorie — darunter die Freigabe.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') { onClose(); return; }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
      if (focusable.length === 0) { event.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const inside = dialogRef.current.contains(document.activeElement);
      if (event.shiftKey && (!inside || document.activeElement === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (!inside || document.activeElement === last)) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const period = review?.period;
  const periodEndInclusive = period ? new Date(Date.parse(period.periodEnd) - 60000) : null;
  const markedLines = review ? review.lines.filter((line) => line.marks.length > 0) : [];
  const shownLines = review ? (onlyMarked ? markedLines : review.lines) : [];
  const visibleControlRows = review
    ? CONTROL_ROWS.filter((row) => !row.hideIfZero || review.checks[row.key]?.stored || review.checks[row.key]?.lines)
    : [];

  return (
    <div className="payout-review-overlay" role="presentation">
      <div className="payout-review-dialog" role="dialog" aria-modal="true" aria-labelledby="payout-review-title" ref={dialogRef}>
        <header className="payout-review-head">
          <div>
            <span className="payout-review-overline"><ListChecks size={13} /> Prüfansicht · nur lesen</span>
            <h2 id="payout-review-title">{period?.companyName || payout.company_name}</h2>
            <p>
              {period
                ? <>{datumLangUndZeit(period.periodStart)} bis {datumLangUndZeit(periodEndInclusive)} · Europe/Berlin</>
                : 'Abrechnung wird geladen…'}
            </p>
          </div>
          <div className="payout-review-head-actions">
            {period && <span className={`payout-review-state is-${period.state}`}>{STATE_LABELS[period.state] || period.state}</span>}
            <button type="button" onClick={load} disabled={loading} aria-label="Prüfansicht neu laden"><RefreshCw size={13} className={loading ? 'is-spinning' : ''} /></button>
            <button type="button" className="payout-review-close" onClick={onClose} aria-label="Prüfansicht schließen" autoFocus><X size={16} /></button>
          </div>
        </header>

        <div className="payout-review-body">
          <p className="payout-review-readonly">Diese Ansicht ändert nichts: keine Freigabe, kein Beleg, keine Buchung. {payout.review_id
            ? 'Alle Zahlen und Namen stammen ausschließlich aus dem gespeicherten Wochen-Prüfstand.'
            : 'Alle Zahlen stammen aus der gespeicherten Abrechnung, ihren Buchungszeilen und den Gebührennachweisen.'}</p>
          {review?.source === 'weekly_snapshot' && <p>Prüfstand {review.revision} · gespeichert am {datumLangUndZeit(review.savedAt)}</p>}

          {loadError && <ErrorBox text="Prüfansicht konnte nicht geladen werden." onRetry={load} />}
          {!review && loading && !loadError && (
            <div className="payout-review-loading" aria-busy="true">
              <Skeleton h={18} /><Skeleton h={120} /><Skeleton h={220} />
            </div>
          )}

          {review && (
            <>
              <Banner review={review} />

              <section className="payout-review-section">
                <h3>So prüfst du</h3>
                <Checklist
                  review={review}
                  windowStart={wochentagKurzMitZeit(period.periodStart)}
                  windowEnd={wochentagKurzMitZeit(periodEndInclusive)}
                  markedCount={markedLines.length}
                />
              </section>

              <section className="payout-review-section">
                <h3>Kontrollsummen</h3>
                <p className="payout-review-explain">Jede Zahl einmal aus der gespeicherten Abrechnung und einmal als Summe der Buchungszeilen.</p>
                <div className="payout-review-table-wrap">
                  <table className="payout-review-control">
                    <thead><tr><th>Position</th><th>Gespeicherte Abrechnung</th><th>Summe der Buchungszeilen</th><th>Abgleich</th></tr></thead>
                    <tbody>
                      {visibleControlRows.map((row) => {
                        if (row.group) return <tr key={row.group} className="payout-review-group"><td colSpan="4">{row.group}</td></tr>;
                        const check = review.checks[row.key] || { stored: null, lines: null, equal: null };
                        return (
                          <tr key={row.key} data-check={row.key} className={[row.indent ? 'is-indent' : '', row.strong ? 'is-strong' : '', check.equal === false ? 'is-bad' : ''].filter(Boolean).join(' ')}>
                            <td>{row.label}{row.rides && <small>{review.rideBreakdown.completed} abgeschlossen · {review.rideBreakdown.cancellationFee} Storno · {review.rideBreakdown.noShowFee} Nichtantritt</small>}</td>
                            <td className="is-num">{formatCheckValue(check.stored, row)}</td>
                            <td className="is-num">{formatCheckValue(check.lines, row)}</td>
                            <td><Verdict check={check} row={row} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="payout-review-section">
                <h3>Je Fahrer</h3>
                <div className="payout-review-table-wrap">
                  <table className="payout-review-drivers">
                    <thead><tr>
                      <th>Fahrer</th><th>Fahrten</th><th>davon Storno</th><th>davon Nichtantritt</th><th>Bar</th>
                      <th>Stornogebühr bar</th><th>Online</th><th>Take + Service</th>
                      <th className="is-wrap">Zahlungs<wbr />dienstleister<wbr />kosten</th>
                      <th>Korrekturen</th><th>Bonus (Info)</th>
                      <th className="is-stacked"><span>Kontobewegung</span>{' '}<span>bei VOOGO</span></th>
                    </tr></thead>
                    <tbody>
                      {review.drivers.length === 0
                        ? <tr><td colSpan="12">Keine Buchungszeilen</td></tr>
                        : review.drivers.map((row) => (
                          <tr key={row.driverId || 'partnerkonto'}>
                            <td>{driverLabel(row)}</td>
                            <td className="is-num">{row.rideCount}</td>
                            <td className="is-num">{row.cancellationFeeCount}</td>
                            <td className="is-num">{row.noShowFeeCount}</td>
                            <td className="is-num">{cents(row.cashCents)}</td>
                            <td className="is-num">{cents(row.feeSettlementCashCents)}</td>
                            <td className="is-num">{cents(row.onlineCents)}</td>
                            <td className="is-num">{cents(row.takeServiceCents)}</td>
                            <td className="is-num">{row.pspFeeCents == null ? '–' : minusCents(row.pspFeeCents)}</td>
                            <td className="is-num">{cents(row.correctionCents)}</td>
                            <td className="is-num">{cents(row.bonusCents)}</td>
                            <td className="is-num"><strong>{cents(row.ledgerEffectCents)}</strong></td>
                          </tr>
                        ))}
                    </tbody>
                    <tfoot><tr>
                      <td>Summe aller Buchungszeilen</td>
                      <td className="is-num">{review.checks.rideCount.lines}</td>
                      <td className="is-num">{review.rideBreakdown.cancellationFee}</td>
                      <td className="is-num">{review.rideBreakdown.noShowFee}</td>
                      <td className="is-num">{cents(review.checks.cashCents.lines)}</td>
                      <td className="is-num">{cents(review.checks.feeSettlementCashCents.lines)}</td>
                      <td className="is-num">{cents(review.checks.onlineCents.lines)}</td>
                      <td className="is-num">{cents(review.checks.takeServiceCents.lines)}</td>
                      <td className="is-num">{review.checks.pspCostCents.lines == null ? '–' : minusCents(review.checks.pspCostCents.lines)}</td>
                      <td className="is-num">{cents(review.checks.correctionCents.lines)}</td>
                      <td className="is-num">{cents(review.checks.bonusCents.lines)}</td>
                      <td className="is-num"><strong>{cents(review.checks.ledgerEffectCents.lines)}</strong></td>
                    </tr></tfoot>
                  </table>
                </div>
              </section>

              <section className="payout-review-section">
                <div className="payout-review-section-head">
                  <h3>Alle Buchungszeilen <small>{review.lines.length}</small></h3>
                  <label className="payout-review-filter">
                    <input type="checkbox" checked={onlyMarked} onChange={(event) => setOnlyMarked(event.target.checked)} />
                    Nur markierte Zeilen ({markedLines.length})
                  </label>
                </div>
                <p className="payout-review-explain">Gelb: Korrektur, Gegenbuchung, Stornogebühr oder Nichtantritt. Zeit in Berlin. Jede Fahrt öffnet sich in einem neuen Tab.</p>
                <div className="payout-review-table-wrap">
                  <table className="payout-review-lines">
                    <thead><tr>
                      <th>Zeit (Berlin)</th><th>Fahrer</th><th>Art</th><th>Zahlart</th><th>Fahrpreis</th><th>Bar</th><th>Online</th>
                      <th>Guthaben</th><th>Take + Service</th>
                      <th className="is-wrap">Zahlungs<wbr />dienstleister<wbr />kosten</th>
                      <th>Korrektur / Bonus</th>
                      <th className="is-stacked"><span>Kontobewegung</span>{' '}<span>bei VOOGO</span></th>
                      <th>Grund</th><th>Fahrt</th>
                    </tr></thead>
                    <tbody>
                      {shownLines.length === 0 && <tr><td colSpan="14">Keine Buchungszeilen</td></tr>}
                      {shownLines.map((line) => {
                        const marked = line.marks.length > 0;
                        const hints = lineHints(line);
                        const extra = [
                          line.correctionCents ? cents(line.correctionCents) : null,
                          line.bonusCents ? `${cents(line.bonusCents)} (Info)` : null,
                          line.tipCents ? `Trinkgeld ${cents(line.tipCents)}` : null,
                        ].filter(Boolean).join(' · ');
                        return (
                          <React.Fragment key={line.id}>
                            <tr className={`payout-review-line${marked ? ' is-marked' : ''}`} data-line={line.id}>
                              <td>{datumUndZeit(line.occurredAt, { jahr: false })}</td>
                              <td>{driverLabel(line)}</td>
                              <td>{EVENT_LABELS[line.eventType] || line.eventType}</td>
                              <td>{paymentLabel(line)}</td>
                              <td className="is-num">{optionalCents(line.grossCents)}</td>
                              <td className="is-num">{optionalCents(line.cashCents)}</td>
                              <td className="is-num">{optionalCents(line.onlineCents)}</td>
                              <td className="is-num">{optionalCents(line.walletCents)}</td>
                              <td className="is-num">{optionalCents(line.takeServiceCents)}</td>
                              <td className="is-num">{line.pspFeeCents == null ? '' : minusCents(line.pspFeeCents)}</td>
                              <td className="is-num">{extra}</td>
                              <td className="is-num"><strong>{cents(line.ledgerEffectCents)}</strong></td>
                              <td className="payout-review-reason">{line.reason || ''}</td>
                              <td><RideLink rideId={line.rideId} /></td>
                            </tr>
                            {hints.length > 0 && (
                              <tr className={`payout-review-hint${marked ? ' is-marked' : ''}`}>
                                <td colSpan="14">{hints.map((hint) => <span key={hint}>{hint}</span>)}</td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
